import { BarChart3, Crosshair, Shield, Trophy, Users, type LucideIcon } from 'lucide-react';
import { NavLink, useLocation } from 'react-router';
import { trackFantasyGa4Event } from '@/hooks/useGa4Tracking';
import { trackFantasyEvent } from '@/analytics/fantasyEventTracking';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/AuthStore';

type NavItem = {
  label: string;
  href: string;
  Icon?: LucideIcon;
  protected?: boolean;
  isActive?: (path: string) => boolean;
};

const navItems: NavItem[] = [
  {
    label: 'My Team',
    href: '/my-team',
    Icon: Shield,
    isActive: path => path === '/' || path === '/my-team' || path.startsWith('/my-team/'),
  },
  {
    label: 'Leaderboard',
    href: '/leaderboard',
    Icon: Trophy,
    protected: true,
  },
  {
    label: 'Leagues',
    href: '/leagues',
    Icon: Users,
    protected: true,
    isActive: path => path === '/leagues' || path.startsWith('/leagues/'),
  },
  {
    label: 'Match Centre',
    href: '/match-centre',
    Icon: Crosshair,
    protected: true,
    isActive: path => path === '/match-centre' || path.startsWith('/match/'),
  },
  {
    label: 'Stats Centre',
    href: '/stats-centre',
    Icon: BarChart3,
    protected: true,
  },
];

const getIsActive = (link: NavItem, path: string) =>
  link.isActive ? link.isActive(path) : path === link.href;

// Pages like Match Centre keep their filters in the query string, so
// re-clicking the page you are already on must not drop them.
const getLinkTarget = (href: string, location: { pathname: string; search: string }) =>
  location.pathname === href ? { pathname: href, search: location.search } : href;

const getMobileActiveLabelWidth = (href: string) => {
  switch (href) {
    case '/match-centre':
    case '/stats-centre':
      return 'max-w-[6.5rem] text-[9px]';
    case '/leaderboard':
      return 'max-w-[5.75rem] text-[9px]';
    default:
      return 'max-w-[5rem] text-[10px]';
  }
};

export const Navbar = () => {
  const location = useLocation();
  const isAuthenticated = useAuthStore(s => s.status === 'authenticated');
  const currentPath = location.pathname;
  const visibleItems = navItems.filter(link => !link.protected || isAuthenticated);
  const activeIndex = visibleItems.findIndex(link => getIsActive(link, currentPath));

  return (
    <>
      <nav className="hidden md:block font-primary">
        <div className="container overflow-x-auto">
          <div className="flex w-full items-center gap-4 md:gap-12">
            <div className="flex min-w-0 flex-1 items-center gap-4 overflow-x-auto md:gap-12">
              {visibleItems.map(link => {
                const isActive = getIsActive(link, currentPath);
                return (
                  <NavLink
                    key={link.href}
                    className={cn(
                      'font-bold border-b-4 transition-all py-3 whitespace-nowrap text-sm md:text-base',
                      isActive ? '' : 'border-transparent'
                    )}
                    to={getLinkTarget(link.href, location)}
                    onClick={() =>
                      trackFantasyEvent('primaryNavigation', { link_name: link.label })
                    }
                  >
                    {link.label}
                  </NavLink>
                );
              })}
            </div>

            <NavLink
              to="/how-to-play"
              onClick={() =>
                trackFantasyGa4Event('rules', {
                  eventCategory: 'FF - How to Play',
                  eventLabel: 'Read the rules',
                })
              }
              className={({ isActive }) =>
                cn(
                  'ml-auto font-bold whitespace-nowrap hidden md:inline-flex items-center justify-end py-3 border-b-4 text-sm md:text-base',
                  isActive ? '' : 'border-transparent'
                )
              }
            >
              How To Play
            </NavLink>
          </div>
        </div>
      </nav>

      <nav
        aria-label="Primary"
        className="md:hidden fixed inset-x-0 bottom-0 z-40 border-t border-black/10 bg-white/70 shadow-[0_-6px_18px_rgba(15,23,42,0.08)] backdrop-blur-xl supports-[backdrop-filter]:bg-white/55"
      >
        <div
          className="mx-auto grid max-w-screen-sm grid-cols-5 items-center gap-1 px-5 pt-2"
          style={{
            paddingBottom: 'calc(0.65rem + env(safe-area-inset-bottom))',
          }}
        >
          {navItems.map((link, index) => {
            const isActive = getIsActive(link, currentPath);
            const Icon = link.Icon;
            const isFirst = index === 0;
            const isLast = index === navItems.length - 1;
            const isLeftOfActive = index === activeIndex - 1;
            const isRightOfActive = index === activeIndex + 1;

            return (
              <div
                key={link.href}
                className={cn(
                  'relative min-w-0',
                  isActive && 'px-1.5',
                  isLeftOfActive && 'pr-2',
                  isRightOfActive && 'pl-2',
                  isFirst && 'pl-2',
                  isLast && 'pr-2'
                )}
              >
                <NavLink
                  aria-current={isActive ? 'page' : undefined}
                  aria-label={link.label}
                  className={cn(
                    'relative flex h-12 w-full items-center justify-center overflow-visible',
                    isActive ? 'text-[#111827]' : 'text-[#6b7280]'
                  )}
                  to={getLinkTarget(link.href, location)}
                  onClick={() => trackFantasyEvent('primaryNavigation', { link_name: link.label })}
                >
                  <span
                    className={cn(
                      'relative z-10 flex h-10 items-center justify-center',
                      isActive
                        ? 'mx-1 rounded-full bg-white px-3 shadow-[0_4px_12px_rgba(15,23,42,0.08)]'
                        : 'w-10'
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-8 w-8 shrink-0 items-center justify-center',
                        isActive ? 'text-[#1e73d1]' : 'text-[#7c7c98]'
                      )}
                    >
                      {Icon ? <Icon className="h-5 w-5" strokeWidth={2.4} /> : null}
                    </span>

                    {isActive ? (
                      <span
                        className={cn(
                          'ml-1 min-w-0 overflow-hidden whitespace-nowrap font-semibold tracking-[-0.01em] text-[#111827]',
                          getMobileActiveLabelWidth(link.href)
                        )}
                      >
                        {link.label}
                      </span>
                    ) : null}
                  </span>
                </NavLink>
              </div>
            );
          })}
        </div>
      </nav>
    </>
  );
};
